import { when, keys } from 'mobx';
import qs from 'qs';
import { types, getParent, flow, getSnapshot, onSnapshot, isAlive } from 'mobx-state-tree';
import { getOr } from 'lodash/fp';
import _, { isEmpty } from 'lodash';
import moment from 'moment';
import { TableData, SingleItemData } from '../../models/Orders';
import DashboardData from './DashboardData';
import FilterValues from './FilterValues';
import { timeRange, STATUS_TITLES } from '../../constants/orders';
import filterKeys from '../../constants/orders/filter-keys';
import { calcAndFormatDate, getInitialFilterValues, getMappedParams } from './utils';
import { AccountsBar, CheckedOrders, ReportsFilters } from '../common';
import downloadFile from '../../utils/downloadFile';

const BASE_URL = 'order/v1/Orders';
const { failed, inProcess, exported } = STATUS_TITLES;

const OrdersStore = types.compose(
  types
    .model('OrdersStore', {
      accountsBar: types.optional(AccountsBar, {}),
      dashboardData: types.optional(DashboardData, {}),
      tableData: types.optional(types.array(TableData), []),
      singleItemData: types.optional(types.map(SingleItemData), {}),
      singleItemLoading: false,
      filterValues: types.optional(FilterValues, getInitialFilterValues()),
      isAdvancedBarOpen: false,
      isActionBarOpen: false,
      checkboxClicked: false,
      tableFilter: '',
      chartFilter: '',
      checkedOrders: types.optional(types.map(types.boolean), {}),
      filterKeys: types.optional(types.array(types.string), filterKeys),
      failedReasons: types.optional(types.array(types.string), []),
      reportsFilters: types.optional(ReportsFilters, {}),
      tableDataLoaded: false,
      sortDescending: types.maybe(types.boolean),
      sortColumn: types.maybe(types.number),
      page: 1,
      limit: 100,
      totalResults: 0,
      allPagesChecked: false,
      importedOrdersCSV: types.optional(types.frozen, {}),
      storeAccountId: types.optional(types.string, ''),
    })
    .views(self => ({
      get filteredTableData() {
        return self.tableData;
      },
      get isBetaUser() {
        return getParent(self).settingsStore.userProfile.isBetaUser;
      },
      get isfileImported() {
        return isEmpty(self.importedOrdersCSV);
      },
      get tableDataAsObject() {
        return _.keyBy(self.tableData, o => o.id);
      },
      get failedOrdersCodesInCheckedArray() {
        return self.checkedOrdersArray.filter(
          code => self.tableDataAsObject[code]?.status === failed,
        );
      },
      get exportedOrdersStatusInCheckedArray() {
        return self.checkedOrdersArray.filter(
          code => self.tableDataAsObject[code]?.status === exported,
        );
      },
      get someCheckedOrderLoading() {
        return self.checkedOrdersArray.some(code => self.tableDataAsObject[code]?.loading ?? false);
      },
      get transport5007() {
        return getParent(self).transport5007;
      },
      get accountCode() {
        return getParent(self).sessionStore.accountCode;
      },
      get marketPlaceId() {
        return getParent(self).settingsStore.markets.defaultDestination.id;
      },
      get accounts() {
        return getParent(self).settingsStore.markets.sellersAccounts;
      },
      get defaultAccountCode() {
        return getOr('', 'settingsStore.markets.defaultSellersAccount.code', getParent(self));
      },
      get browserHistory() {
        return getParent(self).routingStore.browserHistory;
      },
      get checkedOrdersArray() {
        return keys(self.checkedOrders)
          .sort()
          .reverse();
      },
      get ifOrderPathNotInScope() {
        return !self.browserHistory.getCurrentLocation().pathname.includes('orders');
      },
      get start() {
        return (self.page - 1) * self.limit;
      },
      get requestData() {
        const checkAlive = isAlive(self.accountsBar.selectedDestinationMarketplace);
        const data = {
          params: {
            destinationMarketplace: checkAlive
              ? self.accountsBar.selectedDestinationMarketplace?.id
              : self.accountsBar.defaultDestinationMarketplace.id,
            buyerOrderTimeFrom: calcAndFormatDate(timeRange[self.dashboardData.selectedTimeRange]),
            currency: getParent(self).settingsStore.userProfile.defaultCurrencyId,
            sellerAccountId: self.accountsBar.accountsQueryString,
            start: self.start,
            limit: self.limit,
          },
          headers: { 'Account-Id': self.accountCode },
        };
        if (self.tableFilter) {
          data.params.searchText = self.tableFilter;
        }
        if (self.sortDescending !== null && self.sortColumn !== null) {
          data.params.sortDescending = self.sortDescending;
          data.params.sortColumn = self.sortColumn;
        }
        return data;
      },
      get dashboardRequestData() {
        const checkAlive = isAlive(self.accountsBar.selectedDestinationMarketplace);
        return {
          params: {
            destinationMarketplace: checkAlive
              ? self.accountsBar.selectedDestinationMarketplace?.id
              : self.accountsBar.defaultDestinationMarketplace.id,
            buyerOrderTimeFrom: calcAndFormatDate(timeRange[self.dashboardData.selectedTimeRange]),
            currency: getParent(self).settingsStore.userProfile.defaultCurrencyId,
            sellerAccounts: self?.accountsBar?.accountsQueryStringDashboard ?? '',
          },
          headers: { 'Account-Id': self.accountCode },
        };
      },
      get currentLanguage() {
        return getParent(self).settingsStore.userProfile.languageId;
      },

      get selectedOrdersCount() {
        return self.checkedOrders.size;
      },
      get filteredOrdersCount() {
        if (self.tableFilter !== '' || self.chartFilter !== '') {
          return self.filteredTableData.length;
        }
        return 0;
      },
      get showToast() {
        return getParent(self).toastsStore.showToast;
      },
      get enums() {
        return getParent(self).enumsStore.getEnumsList;
      },
    }))
    .volatile(self => ({
      getSingleItem(code) {
        return self.singleItemData.get(code);
      },
    }))
    .actions(self => ({
      afterCreate() {
        onSnapshot(self.accountsBar, () => {
          if (window.location.pathname.indexOf('orders') !== -1) {
            self.fetchInitialData();
          }
        });
      },
      sendImportedOrders: flow(function* sendImportedOrders() {
        const formData = new FormData();
        formData.append('importedOrdersCSV', self.importedOrdersCSV);
        try {
          const { data } = yield self.transport5007.post(
            `/order/v1/orders/non-api-new`,
            formData,
            {},
            {
              'Content-Type': 'multipart/form-data',
              'Seller-Account-Id': self.storeAccountId,
            },
          );
          return data;
        } catch (error) {
          return {
            errorMessage: 'A problem has occurred while importing the orders. Please try again.',
          };
        }
      }),
      fetchInitialData() {
        when(
          () => self.accountsBar.toJSON().selectedDestinationMarketplace,
          () => self.fetchData(),
        );
      },
      setCheckboxClicked(value) {
        self.checkboxClicked = !!value;
      },
      setTableFilter(nextFilter, nextKeys = []) {
        self.tableFilter = nextFilter.target ? nextFilter.target.value : nextFilter;
        self.filterKeys = nextKeys.length ? nextKeys : filterKeys;
      },
      setSorting(column, direction) {
        self.sortColumn = column !== undefined ? column : null;
        self.sortDescending = direction !== undefined ? direction : null;
      },
      setAllPagesChecked(val) {
        self.allPagesChecked = !!val;
      },
      setChartFilter(nextFilter) {
        self.chartFilter = nextFilter;
        if (nextFilter === '' && self.checkedOrders.size !== self.tableData.length) {
          self.allOrdersChecked = false;
        }
      },
      toggleAdvancedBar() {
        self.isAdvancedBarOpen = !self.isAdvancedBarOpen;
        self.filterValues.fetchSourceMarketPlaces();
      },
      toggleActionBar(barStatus) {
        if (typeof barStatus === 'boolean') {
          self.isActionBarOpen = barStatus;
        } else {
          self.isActionBarOpen = !self.isActionBarOpen;
        }
      },
      fetchData() {
        if (!self.accountsBar.selectedSellerAccounts.every(o => !!o)) {
          self.accountsBar.init();
          return;
        }

        if (!self.accountsBar.toJSON().selectedSellerAccounts?.length) {
          self.unmountOrders();
          return;
        }

        self.fetchTableData();
        self.dashboardData.fetchDashboardData();
      },
      fetchAllCheckedItems() {
        if (self.singleItemData.size !== self.checkedOrders.size) {
          self.checkedOrdersArray.forEach(o => {
            if (self.singleItemData.get(o) === undefined) {
              self.fetchSingleItemData(o);
            }
          });
        }
      },
      fetchTableData: flow(function* fetchTableData() {
        try {
          const { headers } = self.requestData;
          let { params } = self.requestData;
          params = { ...params, ...getMappedParams(self.filterValues) };
          if (!params.destinationMarketplace) {
            self.tableDataLoaded = true;
            return;
          }
          const stringParams = qs.stringify(params, { arrayFormat: 'repeat', encode: false });
          self.tableDataLoaded = false;
          const {
            data: { data, totalResults },
          } = yield self.transport5007.fetch(`${BASE_URL}?${stringParams}`, {}, headers);
          self.totalResults = totalResults;
          self.tableData = data;
          self.tableDataLoaded = true;
        } catch (err) {
          self.tableData = [];
          console.log(err);
        }
      }),
      setPage(nextPage) {
        self.page = nextPage;
      },
      fetchNextPage: flow(function* fetchNextPage(nextPage) {
        self.setPage(nextPage);
        if (!self.accountsBar.selectedSellerAccounts.every(o => !!o)) {
          self.accountsBar.init();
          return;
        }
        if (self.allPagesChecked) {
          yield self.fetchTableData();
          self.toggleCheckedAllOrders(true);
        } else {
          yield self.fetchTableData();
        }
      }),
      fetchSingleItemData(code) {
        return new Promise((resolve, reject) => {
          try {
            when(
              () => getParent(self).sessionStore.accountCode !== undefined,
              () => self.getSingleItemData(code).then(item => resolve(item)),
            );
          } catch (err) {
            console.log(err);
            reject(err);
          }
        });
      },
      getSingleItemData: flow(function* getSingleItemData(code) {
        try {
          const { headers } = self.requestData;
          const url = `${BASE_URL}/${code}`;
          const { data } = yield self.transport5007.fetch(url, null, headers);
          self.singleItemData.set(code, data);
          return self.singleItemData.get(code);
        } catch (err) {
          console.log(err);
          return Promise.reject(err);
        }
      }),
      cancelOrder: flow(function* cancelOrder(code) {
        try {
          const url = `${BASE_URL}/${code}/cancel`;
          yield self.transport5007.post(url);
          self.updateSingleAndTableOrderStatus(code, 'pendingCancellation');
        } catch (err) {
          console.log(err);
        }
      }),
      generateReport: flow(function* generateReport() {
        try {
          const requrl = 'order/v1/reports/sales';
          const req = {
            ...self.reportsFilters.toJSON(),
            currency: getParent(self).settingsStore.userProfile.defaultCurrencyId,
            sellerAccountIds: self.accountsBar.selectedSellerAccounts.map(({ id }) => id),
          };
          const { data } = yield self.transport5007.post(requrl, req, { responseType: 'blob' });
          downloadFile(
            data,
            `Sales report ${self.reportsFilters.fromDate} ${self.reportsFilters.toDate}`,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          );
          return true;
        } catch (err) {
          console.log(err);
          return false;
        }
      }),
      unmountOrders() {
        self.singleItemData.clear();
        self.checkedOrders.clear();
        self.tableData = [];
        self.dashboardData = {};
        self.isAdvancedBarOpen = false;
        self.isActionBarOpen = false;
        self.filterValues = getInitialFilterValues();
        self.totalResults = 0;
      },
      switchItem(currentCode, nextItemArrIndex) {
        const currentItemIndex = self.checkedOrdersArray.findIndex(code => code === currentCode);
        const nextItemId = self.checkedOrdersArray[currentItemIndex + nextItemArrIndex];
        const path = `/orders/single-item/${nextItemId}`;
        self.browserHistory.push(path);
      },
      handleReturn() {
        self.browserHistory.push('/orders');
      },
      updateSingleOrder: flow(function* updateSingleOrder(order) {
        try {
          self.singleItemLoading = true;
          yield self.transport5007.update(`${BASE_URL}/${order.id}`, order, self.headers);
          self.singleItemData.set(order.id, getSnapshot(order));
          if (
            self.tableDataAsObject[order.id] &&
            order.status !== self.tableDataAsObject[order.id].status
          ) {
            self.updateSingleAndTableOrderStatus(order.id, order.status);
          }
          return Promise.resolve(true);
        } catch (err) {
          console.log(err);
          return Promise.reject(new Error(err));
        } finally {
          self.singleItemLoading = false;
        }
      }),
      saveAndTryAgain: flow(function* saveAndTryAgain(order) {
        yield self.updateSingleOrder(order);
        yield self.tryAgain([order.id]);
      }),
      tryAgain: flow(function* tryAgain(ordersIds) {
        try {
          const URL = `${BASE_URL}/try-again`;
          ordersIds.forEach(id => {
            self.tableDataAsObject[id].setLoading(true);
          });
          yield self.transport5007.update(URL, ordersIds, {}, self.requestData.headers);
          ordersIds.forEach(id => self.updateSingleAndTableOrderStatus(id));
        } catch (err) {
          console.log(err);
        } finally {
          ordersIds.forEach(id => {
            if (self.tableDataAsObject[id]) {
              self.tableDataAsObject[id].setLoading(false);
            }
          });
        }
      }),
      tryAgainWithNewAsin: flow(function* tryAgainWithNewAsin(orderId, asin) {
        try {
          const URL = `${BASE_URL}/try-again-asin`;
          self.tableDataAsObject[orderId].setLoading(true);
          yield self.transport5007.update(URL, { orderId, asin }, {}, self.requestData.headers);
          self.updateSingleAndTableOrderStatus(orderId);
        } catch (err) {
          console.log(err);
        } finally {
          self.tableDataAsObject[orderId].setLoading(false);
        }
      }),
      forcePurchase: flow(function* forcePurchase(ordersIds) {
        try {
          const URL = `${BASE_URL}/forced`;
          ordersIds.forEach(id => {
            self.tableDataAsObject[id].setLoading(true);
          });
          yield self.transport5007.update(URL, ordersIds, {}, self.requestData.headers);
          ordersIds.forEach(id => self.updateSingleAndTableOrderStatus(id));
        } catch (err) {
          console.log(err);
        } finally {
          ordersIds.forEach(id => {
            self.tableDataAsObject[id].setLoading(false);
          });
        }
      }),
      forceMarketplacePurchase: flow(function* forceMarketplacePurchase(ordersIds) {
        try {
          const URL = `${BASE_URL}/forceMarketplace`;
          ordersIds.forEach(id => {
            self.tableDataAsObject[id].setLoading(true);
          });
          yield self.transport5007.update(URL, ordersIds, {}, self.requestData.headers);
          ordersIds.forEach(id => self.updateSingleAndTableOrderStatus(id));
        } catch (err) {
          console.log(err);
        } finally {
          ordersIds.forEach(id => {
            self.tableDataAsObject[id].setLoading(false);
          });
        }
      }),
      forcePush: flow(function* forcePush(orderId) {
        try {
          self.tableDataAsObject[orderId].setLoading(true);
          const URL = `${BASE_URL}/force-queue-order/${orderId}`;
          yield self.transport5007.fetch(URL, {}, {}, self.requestData.headers);
          yield self.fetchTableData();
          self.showToast({
            type: self.enums.TOAST_TYPES.INFO,
            message: 'Order Successfully Pushed',
          });
        } catch (err) {
          console.log(err);
          self.showToast({
            type: self.enums.TOAST_TYPES.WARNING,
            message: 'Force Push Failed',
          });
        } finally {
          self.tableDataAsObject[orderId].setLoading(false);
        }
      }),
      exportOrder: flow(function* exportOrder(ordersIds) {
        try {
          const URL = `${BASE_URL}/export`;
          const fullPayload = {};
          if (self.allPagesChecked) {
            const { start, limit, ...reqData } = self.requestData.params;
            fullPayload.filter = {
              ...getMappedParams(self.filterValues),
              ...reqData,
              sellerAccountId: (self?.accountsBar?.selectedSellerAccounts ?? []).map(
                ({ id }) => id,
              ),
            };
            fullPayload.allPagesChecked = true;
          } else {
            fullPayload.allPagesChecked = false;
            fullPayload.orderIds = ordersIds;
          }
          // starts loading items from the current page, we don't have access to the others
          ordersIds.forEach(id => {
            self.tableDataAsObject[id].setLoading(true);
          });
          const { data } = yield self.transport5007.post(
            URL,
            fullPayload,
            {},
            self.requestData.headers,
          );
          downloadFile(data, `Order export ${moment().format('MMM D YYYY HH:mm')}.csv`, 'text/csv');
          // updates items from the current page, we don't have access to the others
          ordersIds.forEach(orderId => {
            self.tableDataAsObject[orderId].updateStatus(exported);
          });
        } catch (err) {
          console.log(err);
        } finally {
          ordersIds.forEach(id => {
            self.tableDataAsObject[id].setLoading(false);
          });
        }
      }),
      updateSingleAndTableOrderStatus(id, status = inProcess) {
        self.dashboardData.fetchDashboardData();
        self.chartFilter = '';
        self.tableDataAsObject[id].updateStatus(status);
        const singleItem = self.singleItemData.get(id);
        if (singleItem) {
          singleItem.setStatus(status);
        }
      },
      clearFilters() {
        self.chartFilter = '';
        self.tableFilter = '';
        self.dashboardData.resetTooltip();
        self.sortColumn = null;
        self.sortDescending = null;
      },
      setStoreAccountId: value => {
        self.storeAccountId = value;
      },
      setImportedOrdersCSV: value => {
        self.importedOrdersCSV = value;
      },
    })),
  CheckedOrders,
);

export default OrdersStore;
